/**
 * Location and time section: venue, address, map, end time, max attendees, recurring.
 */

import { type ReactNode } from "react";
import {
  InlineTextField,
  InlineTextPreview,
  InlineLinkField,
  InlineStepper,
  InlineToggle,
  InlinePickerRow,
  ExpandablePicker,
} from "../InlineFields.tsx";
import { SeriesBuilder } from "../SeriesBuilder.tsx";
import type { SeriesConfig } from "../SeriesBuilder.tsx";
import { SmartSection } from "../SmartSection.tsx";

export interface LocationSectionProps {
  id: string;
  venue: string;
  setVenue: (v: string) => void;
  address: string;
  setEditingField: (f: "address" | null) => void;
  mapsUrl: string;
  setMapsUrl: (v: string) => void;
  maxAttendees: number | null;
  setMaxAttendees: (v: number | null) => void;
  isRecurring: boolean;
  setIsRecurring: (v: boolean) => void;
  rrule: string;
  setRrule: (v: string) => void;
  seriesConfig: SeriesConfig | null;
  setSeriesConfig: (v: SeriesConfig | null) => void;
  homeGroupId?: string | null;
  setHomeGroupId?: (id: string | null) => void;
  homeGroups?: { id: string; name: string }[];
  showHomeGroupPicker?: boolean;
  setShowHomeGroupPicker?: (v: boolean) => void;
  requiresRsvpConfirmation?: boolean;
  setRequiresRsvpConfirmation?: (v: boolean) => void;
  icon: ReactNode;
  summary: string;
  filledCount: number;
  totalCount: number;
  hint?: string;
}

export function LocationSection({
  id,
  venue,
  setVenue,
  address,
  setEditingField,
  mapsUrl,
  setMapsUrl,
  maxAttendees,
  setMaxAttendees,
  isRecurring,
  setIsRecurring,
  rrule,
  setRrule,
  seriesConfig,
  setSeriesConfig,
  homeGroupId,
  setHomeGroupId,
  homeGroups = [],
  showHomeGroupPicker,
  setShowHomeGroupPicker,
  requiresRsvpConfirmation = false,
  setRequiresRsvpConfirmation,
  icon,
  summary,
  filledCount,
  totalCount,
  hint,
}: LocationSectionProps) {
  const homeGroupOptions = [
    { value: "", label: "Нет" },
    ...homeGroups.map((g) => ({ value: g.id, label: g.name })),
  ];
  const currentHomeGroupLabel =
    homeGroupOptions.find((o) => o.value === (homeGroupId ?? ""))?.label ??
    "Нет";

  return (
    <SmartSection
      id={id}
      title="Место"
      icon={icon}
      summary={summary}
      filledCount={filledCount}
      totalCount={totalCount}
      hint={hint}
      defaultOpen={false}
    >
      <div className="card overflow-hidden mb-4">
        {setHomeGroupId && setShowHomeGroupPicker && (
          <>
            <InlinePickerRow
              label="Домашняя группа"
              value={currentHomeGroupLabel}
              onClick={() => setShowHomeGroupPicker(!showHomeGroupPicker)}
              isOpen={showHomeGroupPicker ?? false}
            />
            <ExpandablePicker
              isOpen={showHomeGroupPicker ?? false}
              options={homeGroupOptions}
              value={homeGroupId ?? ""}
              onChange={(v) => {
                setHomeGroupId(v || null);
                setShowHomeGroupPicker(false);
              }}
              onClose={() => setShowHomeGroupPicker(false)}
            />
          </>
        )}
        <InlineTextField
          label="Место"
          value={venue}
          onChange={setVenue}
          placeholder="Название места"
        />
        <InlineTextPreview
          label="Адрес"
          value={address}
          placeholder="Добавить..."
          onClick={() => setEditingField("address")}
        />
        <InlineLinkField
          label="Карта"
          value={mapsUrl}
          onChange={setMapsUrl}
          placeholder="https://maps.google.com/..."
        />
        <InlineStepper
          label="Участников"
          value={maxAttendees}
          onChange={setMaxAttendees}
          min={1}
          max={10000}
          step={10}
          defaultValue={50}
          placeholder="∞"
        />
        {setRequiresRsvpConfirmation && (
          <InlineToggle
            label="Требует подтверждения"
            description="Регистрация ожидает подтверждения организатором. Место и карта доступны после подтверждения."
            checked={requiresRsvpConfirmation}
            onChange={setRequiresRsvpConfirmation}
          />
        )}
        <InlineToggle
          label="Повторяющееся"
          description="Создать серию мероприятий"
          checked={isRecurring}
          onChange={setIsRecurring}
        />
      </div>
      {isRecurring && (
        <SeriesBuilder
          initialRrule={rrule}
          initialConfig={seriesConfig ?? undefined}
          onChange={(newRrule, newConfig) => {
            setRrule(newRrule);
            setSeriesConfig(newConfig ?? null);
          }}
        />
      )}
    </SmartSection>
  );
}
